import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Map as MapIcon, X } from 'lucide-react';
import MedjimurjeMap, { MEDJIMURJE_PLACES, MEDJIMURJE_REGIONS } from './MedjimurjeMap';
import MedjimurjeMapV2 from './MedjimurjeMapV2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXTERNAL_NAV = {
  svicarska: { q: 'Švicarska', label: 'Švicarska' },
  svijet:    { q: 'Svijet',    label: 'Svijet', to: '/kategorija/vijesti-svijet' },
  varazdin:  { q: 'Varaždin',  label: 'Varaždin', to: '/kategorija/varazdinske-vijesti' },
  hrvatska:  { q: 'Hrvatska',  label: 'Hrvatska', to: '/kategorija/hrvatska-vijesti' },
};

/**
 * Public County Map widget — desktop: news filter renders BELOW the map
 * (per user request) so the map gets full width and is bigger; mobile:
 * map first, then filter list. Switch is via responsive flex column.
 */
export default function CountyMapWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [variant, setVariant] = useState('v1');
  const [activePlace, setActivePlace] = useState(null);
  const [activeRegion, setActiveRegion] = useState(null);
  const [activeExternal, setActiveExternal] = useState(null);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    fetch(`${API}/public/county-map`).then(r => r.ok ? r.json() : null).then(d => {
      if (!d) return;
      setEnabled(!!d.widget_enabled);
      setVariant(d.variant === 'v2' ? 'v2' : 'v1');
      setData(d);
    }).catch(() => {});
  }, []);

  const placeCounts = useMemo(() => {
    const map = {};
    (data?.items || []).forEach(i => { map[i.county] = i.articles; });
    return map;
  }, [data]);

  async function loadPlace(name) {
    setActivePlace(name); setActiveRegion(null); setActiveExternal(null);
    try {
      const r = await fetch(`${API}/public/articles?q=${encodeURIComponent(name)}&limit=12`);
      if (r.ok) { const d = await r.json(); setArticles(d.articles || []); }
    } catch { setArticles([]); }
  }

  async function loadRegion(regionKey) {
    setActiveRegion(regionKey); setActivePlace(null); setActiveExternal(null);
    const region = MEDJIMURJE_REGIONS.find(r => r.key === regionKey);
    if (!region) return;
    try {
      const r = await fetch(`${API}/public/articles?q=${encodeURIComponent(region.places[0].name)}&limit=12`);
      if (r.ok) { const d = await r.json(); setArticles(d.articles || []); }
    } catch { setArticles([]); }
  }

  function onSelectExternal(key) {
    const nav = EXTERNAL_NAV[key];
    if (!nav) return;
    if (nav.to) navigate(nav.to);
    else navigate(`/pretraga?q=${encodeURIComponent(nav.q)}`);
  }

  function clearFilter() { setActivePlace(null); setActiveRegion(null); setActiveExternal(null); setArticles([]); }

  if (!enabled || !data) return null;
  const totalArticles = data.total || 0;
  const totalPlaces = MEDJIMURJE_PLACES.length;

  return (
    <section className="mt-10 bg-white dark:bg-slate-900/60 border border-gray-200 dark:border-slate-800 rounded-2xl overflow-hidden" data-testid="county-map-widget">
      <div className="px-5 py-4 border-b border-gray-200 dark:border-slate-800 flex items-center gap-2">
        <MapIcon size={18} className="text-red-600" />
        <h2 className="text-sm font-bold uppercase tracking-wider text-gray-900 dark:text-gray-100">{t('map.title')}</h2>
        <span className="ml-auto text-xs text-gray-500 dark:text-gray-400" data-testid="county-map-summary">
          {t('map.summary', { articles: totalArticles, cities: totalPlaces })}
        </span>
      </div>

      {/* Mobile: external buttons row above map (readable both themes). Desktop: external corner regions in the SVG itself + this row stays for accessibility. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 md:hidden border-b border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-950/50" data-testid="county-map-corners-mobile">
        {Object.entries(EXTERNAL_NAV).map(([key, info]) => (
          <button key={key} onClick={() => onSelectExternal(key)}
                  data-testid={`county-corner-mobile-${key}`}
                  className="px-3 py-2 rounded-lg text-xs font-bold border border-gray-300 dark:border-slate-700
                             text-gray-900 dark:text-gray-100
                             bg-white dark:bg-slate-800/80
                             hover:border-red-500 hover:text-red-600 dark:hover:text-red-400
                             transition-colors">
            <span className="mr-1">{cornerEmoji(key)}</span>{info.label}
          </button>
        ))}
      </div>

      {/* Desktop: large map full width; mobile: map then filter list */}
      <div className="flex flex-col">
        <div className="bg-slate-50 dark:bg-slate-950 p-3 md:p-5">
          <div className="max-w-4xl mx-auto">
            {variant === 'v2' ? (
              <MedjimurjeMapV2
                placeCounts={placeCounts}
                activePlace={activePlace}
                activeExternal={activeExternal}
                onSelect={loadPlace}
                onSelectRegion={loadRegion}
                onSelectExternal={(k) => { setActiveExternal(k); onSelectExternal(k); }}
              />
            ) : (
              <MedjimurjeMap
                placeCounts={placeCounts}
                activePlace={activePlace}
                activeExternal={activeExternal}
                onSelect={loadPlace}
                onSelectRegion={loadRegion}
                onSelectExternal={(k) => { setActiveExternal(k); onSelectExternal(k); }}
              />
            )}
          </div>
          <div className="mt-3 flex items-center justify-center gap-3 text-[11px] text-gray-700 dark:text-gray-300 flex-wrap">
            <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-green-300" /> {t('map.gornje')}</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-300" /> {t('map.srednje')}</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-blue-300" /> {t('map.donje')}</span>
          </div>
        </div>
        {/* Filter / articles panel (always BELOW map on desktop AND mobile) */}
        <div className="border-t border-gray-200 dark:border-slate-800 p-4 max-h-[460px] overflow-y-auto" data-testid="county-map-panel">
          {!activePlace && !activeRegion && (
            <p className="text-xs text-gray-500 dark:text-gray-400 py-6 text-center">{t('map.no_match')}</p>
          )}
          {(activePlace || activeRegion) && (
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100 dark:border-slate-800">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-900 dark:text-gray-100">
                {activePlace || MEDJIMURJE_REGIONS.find(r => r.key === activeRegion)?.label}
              </p>
              <button onClick={clearFilter} className="text-gray-400 hover:text-red-600" aria-label="Clear" data-testid="county-map-clear">
                <X size={14} />
              </button>
            </div>
          )}
          {(activePlace || activeRegion) && articles.length === 0 && (
            <p className="text-xs text-gray-400 py-6 text-center">Nema članaka.</p>
          )}
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {articles.map(a => (
              <li key={a.id} className="group">
                <Link to={`/clanak/${a.slug}`} className="block" data-testid={`county-article-${a.id}`}>
                  <p className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 group-hover:text-red-600 line-clamp-2 leading-snug">{a.title_hr}</p>
                  {a.publish_at && <p className="text-[10px] text-gray-500 mt-0.5">{(a.publish_at || '').slice(0, 10)}</p>}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function cornerEmoji(key) {
  return ({
    svicarska: '🇨🇭', svijet: '🌍', varazdin: '🏰', hrvatska: '🇭🇷',
  })[key] || '';
}
